import React, { useState, useMemo } from 'react';
import { 
  Download, FileText, Search, ChevronDown, 
  Inbox, Info, ShieldCheck, RefreshCw, 
  FolderOpen, Folder
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * UTILIDAD: Detecta el tipo de archivo y asigna estilos visuales
 */
const getFileInfo = (url = '') => {
  const extension = url.split('.').pop().toLowerCase().split('?')[0];
  
  const map = {
    pdf: { color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-100' },
    xls: { color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100' },
    xlsx: { color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100' },
    doc: { color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100' },
    docx: { color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100' },
    zip: { color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100' },
    default: { color: 'text-slate-500', bg: 'bg-slate-50', border: 'border-slate-100' }
  };
  
  return map[extension] || map.default;
};

/**
 * SUB-COMPONENTE: Fila de Documento
 */
const DocumentRow = ({ doc, onDownload }) => {
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const fileInfo = getFileInfo(doc.url);

  return (
    <div className="border-b border-slate-100 last:border-0">
      <div className="flex items-center justify-between p-4 hover:bg-slate-50/80 transition-colors group">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className={`p-2 rounded-lg ${fileInfo.bg} ${fileInfo.color} border ${fileInfo.border}`}>
            <FileText size={20} />
          </div>
          <div className="min-w-0">
            <h4 className="font-semibold text-slate-700 truncate group-hover:text-blue-600 transition-colors">
              {doc.title}
            </h4>
            <div className="flex items-center gap-3 mt-0.5 text-[11px] text-slate-400 font-bold uppercase tracking-wider">
              {doc.subtitle && <span>{doc.subtitle}</span>}
              {doc.subtitle && <span>•</span>}
              <span>{doc.url?.split('.').pop().toUpperCase() || 'Archivo'}</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {doc.description && (
            <button 
              onClick={() => setIsDetailOpen(!isDetailOpen)}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
            >
              <Info size={18} />
            </button>
          )}
          <button 
            onClick={() => onDownload && onDownload(doc)}
            className="p-2 bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white rounded-lg transition-all shadow-sm"
          >
            <Download size={18} />
          </button>
        </div>
      </div>
      
      <AnimatePresence>
        {isDetailOpen && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden bg-slate-50/50"
          >
            <div className="px-14 pb-4 text-sm text-slate-500 italic border-l-2 border-blue-200 ml-6 mb-2">
              {doc.description}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * SUB-COMPONENTE: Acordeón
 */
const CategoryAccordion = ({ title, docs, onDownload, isOpen, onToggle }) => {
  if (docs.length === 0) return null;

  return (
    <div className="mb-4 bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <button 
        onClick={onToggle}
        className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-xl ${isOpen ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'} transition-colors shadow-sm`}>
            {isOpen ? <FolderOpen size={20} /> : <Folder size={20} />}
          </div>
          <div>
            <h3 className="font-bold text-slate-800 tracking-tight">{title}</h3>
            <p className="text-xs text-slate-400 font-semibold uppercase tracking-tighter">
              {docs.length} {docs.length === 1 ? 'Documento' : 'Documentos'}
            </p>
          </div>
        </div>
        <motion.div animate={{ rotate: isOpen ? 180 : 0 }} className="text-slate-400">
          <ChevronDown size={20} />
        </motion.div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className="overflow-hidden border-t border-slate-100"
          >
            <div className="bg-white">
              {docs.map((doc, idx) => (
                <DocumentRow key={doc.id || idx} doc={doc} onDownload={onDownload} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * COMPONENTE REUTILIZABLE: DocumentRepository
 * @param {Array} items - Arreglo de objetos [{ title, category, url, subtitle?, description? }]
 * @param {String} title - Título del encabezado
 * @param {String} subtitle - Badge superior
 * @param {Function} onDownload - Acción al descargar
 */
export const DocumentRepository2 = ({ 
  items = [], 
  title = "Repositorio", 
  subtitle = "Documentación",
  onDownload 
}) => {
  const [search, setSearch] = useState('');
  const [openCategories, setOpenCategories] = useState({});

  const groupedDocs = useMemo(() => {
    const filtered = items.filter(d => 
      d.title.toLowerCase().includes(search.toLowerCase()) || 
      (d.category && d.category.toLowerCase().includes(search.toLowerCase()))
    );

    return filtered.reduce((acc, doc) => {
      const cat = doc.category || 'Documentos Especiales';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(doc);
      return acc;
    }, {});
  }, [items, search]);

  const toggleCategory = (cat) => {
    setOpenCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  return (
    <div className="w-full bg-slate-100 rounded-3xl p-6 md:p-8 font-sans text-slate-900 shadow-inner">
      <div className="max-w-4xl mx-auto">
        <header className="mb-10 text-center">
          {/* <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-50 text-blue-600 rounded-full text-[10px] font-black uppercase tracking-widest border border-blue-100 mb-4 shadow-sm">
            <ShieldCheck size={14} /> {subtitle}
          </div> 
          <h2 className="text-3xl font-black text-slate-900 tracking-tight">{title}</h2>
          */}
          <div className="relative max-w-xl mx-auto mt-8">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input 
              type="text" 
              placeholder="Buscar documentos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-6 py-3.5 bg-white border border-slate-200 rounded-2xl shadow-sm focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 outline-none transition-all placeholder:text-slate-400"
            />
          </div>
        </header>

        <div className="min-h-[200px]">
          {Object.keys(groupedDocs).length > 0 ? (
            Object.entries(groupedDocs).map(([category, docs], idx) => (
              <CategoryAccordion 
                key={category}
                title={category}
                docs={docs}
                onDownload={onDownload}
                isOpen={openCategories[category] ?? idx === 0}
                onToggle={() => toggleCategory(category)}
              />
            ))
          ) : (
            <div className="py-20 text-center bg-white rounded-3xl border-2 border-dashed border-slate-200">
              <Inbox className="text-slate-200 mx-auto mb-3" size={48} />
              <p className="text-slate-400 text-sm font-medium">No se encontraron resultados</p>
            </div>
          )}
        </div>

        <footer className="mt-12 pt-6 border-t border-slate-200 flex justify-between items-center text-[10px] font-bold text-slate-400 uppercase tracking-widest">
          <span className="flex items-center gap-1.5"><RefreshCw size={12} className="text-blue-400 animate-spin-[8s]"/> Actualizado</span>
          <span>© {new Date().getFullYear()}</span>
        </footer>
      </div>
    </div>
  );
};